/**
 * Readable text in a gym's brand color. A gym picks any color, and a pale one
 * (yellow, light teal) is unreadable as text on white or on a light tint of
 * itself. `brandTextColor` keeps the gym's hue but darkens it just enough to
 * clear the WCAG AA 4.5:1 ratio for normal text. `readableTextOn` picks the
 * label color for a solid gym-color fill (see its note).
 */

import { DEFAULT_PRIMARY_COLOR } from '@/utils/constants/brandingDefaults';

const HEX = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
const AA_TEXT_RATIO = 4.5;
/** Callers pass hex-alpha tints (`1a` is 10.2%, not 10%) as round percents, so aim a hair above AA. */
const AA_TEXT_TARGET = AA_TEXT_RATIO + 0.05;

/** Tailwind `bg-gray-50`, the row and page background on the public class pages. */
export const SURFACE_GRAY_50 = '#f9fafb';

/** A 3- or 6-digit hex color, normalised to 6 digits; anything else → null. */
export function normalizeHexColor(input: string | null | undefined): string | null {
  if (!input) return null;
  const v = input.trim();
  if (!HEX.test(v)) return null;
  if (v.length === 4) {
    return `#${v[1]}${v[1]}${v[2]}${v[2]}${v[3]}${v[3]}`.toLowerCase();
  }
  return v.toLowerCase();
}

/** WCAG relative luminance of a 6-digit hex color. */
export function relativeLuminance(hex6: string): number {
  const channel = (i: number) => {
    const c = parseInt(hex6.slice(i, i + 2), 16) / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(1) + 0.7152 * channel(3) + 0.0722 * channel(5);
}

/** WCAG contrast ratio between two 6-digit hex colors (1 to 21). */
export function contrastRatio(hexA: string, hexB: string): number {
  const a = relativeLuminance(hexA);
  const b = relativeLuminance(hexB);
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** White labels stay white down to this ratio. */
const WHITE_LABEL_FLOOR = 3;

/**
 * Text color for a solid `hex` fill. White whenever it reaches 3:1, so red,
 * pink, and mid blues keep the white labels gyms expect; near-black only on
 * pale fills (yellow, light teal, lime) where white washes out. This is a
 * brand choice, not full WCAG AA: small white labels on a mid-tone fill can
 * sit between 3:1 and 4.5:1 (for example 4.0:1 on #ff0000).
 */
export function readableTextOn(hex: string | null | undefined): '#ffffff' | '#111827' {
  const normalized = normalizeHexColor(hex) ?? DEFAULT_PRIMARY_COLOR;
  return contrastRatio(normalized, '#ffffff') >= WHITE_LABEL_FLOOR ? '#ffffff' : '#111827';
}

/** A near-white color sits within this contrast of white... */
const NEAR_WHITE_MAX_RATIO = 1.3;
/** ...and has no channel darker than this, which keeps pale saturated colors. */
const NEAR_WHITE_MIN_CHANNEL = 190;

/**
 * A gym color too close to white to show on the white pages and emails it is
 * painted on: 1.3:1 or less against white AND every RGB channel at 190 or
 * more. White, off-white, cream, pastels such as #fef9c3 or #dbeafe, and pale
 * gray match. Yellow (#ffff00, #fde047) and light teal (#99f6e4) are just as
 * light but have a strongly colored channel, so they still read as a colored
 * button and are not matched.
 */
export function isNearWhite(hex: string | null | undefined): boolean {
  const normalized = normalizeHexColor(hex);
  if (!normalized) return false;
  const channels = [1, 3, 5].map((i) => parseInt(normalized.slice(i, i + 2), 16));
  return (
    Math.min(...channels) >= NEAR_WHITE_MIN_CHANNEL &&
    contrastRatio(normalized, '#ffffff') <= NEAR_WHITE_MAX_RATIO
  );
}

/**
 * The color to paint as the gym's primary. A near-white primary becomes the
 * gym's secondary color, or the CheerVue default when that is near-white too.
 * Any other value is returned unchanged (including null, so callers keep their
 * own defaults).
 */
export function usablePrimaryColor<T extends string | null | undefined>(
  primary: T,
  secondary: string | null | undefined
): T | string {
  if (!isNearWhite(primary)) return primary;
  const fallback = normalizeHexColor(secondary);
  return fallback && !isNearWhite(fallback) ? fallback : DEFAULT_PRIMARY_COLOR;
}

const toHex = (channels: number[]) =>
  `#${channels.map((c) => Math.round(c).toString(16).padStart(2, '0')).join('')}`;

const channelsOf = (hex6: string) => [1, 3, 5].map((i) => parseInt(hex6.slice(i, i + 2), 16));

/**
 * `color-mix(in srgb, <hex> <percent>%, white)` as a hex color — the light
 * tinted backgrounds the public class pages paint behind gym-color text.
 */
export function tintWithWhite(hex6: string, percent: number): string {
  return mixOver(hex6, percent, '#ffffff');
}

/** `color-mix(in srgb, <hex> <percent>%, <surface>)` as a hex color. */
function mixOver(hex6: string, percent: number, surface6: string): string {
  const p = Math.min(Math.max(percent, 0), 100) / 100;
  const under = channelsOf(surface6);
  return toHex(channelsOf(hex6).map((c, i) => c * p + under[i] * (1 - p)));
}

/**
 * The gym color to use for TEXT on white or on a `tintPercent`% tint of the
 * same color. `surface` is what the tint sits on (white unless given, e.g.
 * `#f9fafb` for a gray-50 row). Returns the color unchanged when it already
 * reads at AA_TEXT_TARGET (4.55:1); otherwise darkens it in small steps until
 * it does. A value
 * that is not a hex color (a CSS variable, say) is returned as given.
 */
export function brandTextColor(color: string, tintPercent = 0, surface = '#ffffff'): string {
  const hex = normalizeHexColor(color);
  if (!hex) return color;
  const background = mixOver(hex, tintPercent, normalizeHexColor(surface) ?? '#ffffff');
  const base = channelsOf(hex);
  for (let step = 0; step <= 20; step += 1) {
    const candidate = toHex(base.map((c) => c * (1 - step / 20)));
    if (contrastRatio(candidate, background) >= AA_TEXT_TARGET) return candidate;
  }
  return '#000000';
}
